import * as THREE from 'three';
import {
    convertShelfPositionToPercentage,
    getSimpleShelfSetBack,
    reduceMaterialOpacity,
} from 'components/customer/Preview3D/helpers';
import {
    attachSegmentLineWireframeToggle,
    bindTextureWhenReady,
    getPlaceholderTexture,
    isTextureReady,
    setSegmentLineTransparency,
} from 'components/customer/Preview3D/helpers/Three';
import {
    AdvancedLowerShelf,
    AdvancedShelf,
    AdvancedMiddleShelf,
    AdvancedUpperShelf,
    PreviewFormValues,
} from 'components/customer/Preview3D/types';
import {TemplateVariable} from 'components/customer/Preview3D/usePreview3DData';

// A pantry shelf's offset only ever recesses it relative to the (offset-free)
// partition; a negative offset would push the shelf deeper than the
// partition and make it protrude past the cabinet face, so it's clamped here.
const clampShelfOffset = (shouldApply: boolean, offset: number) =>
    shouldApply ? Math.max(0, offset) : 0;

// Interior-shelf count for a shaped (L / corner) carcase panel. simple_shelves_amount
// is authoritative when present and matches the mini preview; the shelves-array
// fallback only covers products that never carry that field.
export const resolveShapedShelfCount = (
    values: Pick<PreviewFormValues, 'simple_shelves_amount' | 'shelves'>
): number =>
    values.simple_shelves_amount != null
        ? Number(values.simple_shelves_amount)
        : (values.shelves?.length ?? 0);

const LS_SHAPE_SHELF_INSERT = 14;

const isLsShapeShelfInsert = (shelf: AdvancedShelf | undefined): boolean =>
    Number(shelf?.shelf_type) === LS_SHAPE_SHELF_INSERT;

export const getYawedLabelStart = (
    centerX: number,
    centerY: number,
    labelWidth: number,
    rotationY: number
): {x: number; y: number} => ({
    x: centerX - (labelWidth / 2) * Math.cos(rotationY),
    y: centerY - (labelWidth / 2) * Math.sin(rotationY),
});

const resolveShelfSetBack = (
    values: Pick<
        PreviewFormValues,
        'shelf_set_back' | 'simple_shelf_type' | 'shelves'
    >,
    shelfOffset?: number | string | null
): number => {
    const offset = Number(shelfOffset);
    if (shelfOffset !== null && shelfOffset !== '' && Number.isFinite(offset))
        return offset;

    return getSimpleShelfSetBack(values);
};

export const hasUsableShelfPositions = (
    shelves: ({shelf_position: number} | undefined)[] | undefined,
    shelvesCount: number,
    height: number
): boolean => {
    if (!shelves || shelves.length !== shelvesCount || shelvesCount === 0)
        return false;

    const seen = new Set<number>();
    for (const shelf of shelves) {
        const position = convertShelfPositionToPercentage(
            Number(shelf?.shelf_position),
            height
        );
        if (!(position > 0) || !(position < 1)) return false;
        const rounded = Math.round(position * 1000);
        if (seen.has(rounded)) return false;
        seen.add(rounded);
    }

    return true;
};

export type ApplyCarcaseMaterialParams = {
    geometry: THREE.ExtrudeGeometry;
    texture: THREE.Texture;
    edgeTexture: THREE.Texture;
    useSubstrateEdge?: boolean;
    flipGrain?: boolean;
    hasHorizontalGrain?: boolean;
    showTexture?: boolean;
    nonSupplyCarcase?: boolean;
    carcaseSubstrateMaterial?: THREE.MeshStandardMaterial;
};

export const createLineSegments = <
    G extends THREE.BufferGeometry,
    M extends THREE.Material | THREE.Material[],
>(
    mesh: THREE.Mesh<G, M>,
    options?: {
        color?: THREE.ColorRepresentation;
        nonSupplyCarcase?: boolean;
        showTexture?: boolean;
    }
) => {
    const edges = new THREE.EdgesGeometry(mesh.geometry);

    const lineMaterial = new THREE.LineBasicMaterial({
        color: options?.color ?? '#333',
    });

    if (options?.nonSupplyCarcase) reduceMaterialOpacity(lineMaterial);

    const lineSegments = new THREE.LineSegments(edges, lineMaterial);

    // Fade the outline to match the rectangular panels, except when the faces
    // aren't drawn or it's a non-supply carcase (which has its own opacity).
    // Wireframe mode is re-applied through onToggleWireframe after render.
    if ((options?.showTexture ?? true) && !options?.nonSupplyCarcase) {
        setSegmentLineTransparency(lineMaterial);
        attachSegmentLineWireframeToggle(lineSegments);
    }

    return lineSegments;
};

// Apply a positive polygonOffset to the panel mesh's material(s) so the
// panel face is nudged backward in the depth buffer just enough to lose to
// its own outline at coplanar wall contact, without affecting self-occlusion
// of the back-side outline. Applied to all cabinet panels (base, upper, and
// L/Key/U shapes) preserves the wall-facing outline without the x-ray
// look that comes from disabling depth testing on the line.
export const applyWallFlushOutlineFix = (mesh: THREE.Mesh) => {
    const apply = (m: THREE.Material | null) => {
        // Material arrays can hold null for faces that aren't drawn.
        if (!m) return;
        m.polygonOffset = true;
        m.polygonOffsetFactor = 0.3;
        m.polygonOffsetUnits = 0.3;
    };
    if (Array.isArray(mesh.material)) {
        mesh.material.forEach(apply);
    } else {
        apply(mesh.material);
    }
};

export const attachWireframeToggleEvent = (mesh: THREE.Mesh) => {
    mesh.userData.onToggleWireframe = (isWireframeMode: boolean) => {
        if (mesh.material instanceof Array) {
            mesh.material.forEach((mat: THREE.Material) => {
                mat.visible = !isWireframeMode;
            });
        } else {
            mesh.material.visible = !isWireframeMode;
        }
    };
};

export const applyCarcaseMaterial = ({
    geometry,
    texture,
    edgeTexture,
    useSubstrateEdge,
    flipGrain,
    hasHorizontalGrain = true,
    showTexture = true,
    nonSupplyCarcase = false,
    carcaseSubstrateMaterial,
}: ApplyCarcaseMaterialParams) => {
    const configureCarcaseFace = (tex: THREE.Texture) => {
        tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
        tex.offset.set(0, 0.5);
        tex.repeat.set(0.003, 0.003);

        const showHorizontalGrain = flipGrain
            ? !hasHorizontalGrain
            : hasHorizontalGrain;

        tex.rotation = showHorizontalGrain ? (Math.PI / 4) * 2 : 0;
        tex.center = new THREE.Vector2(0.5, 0.5);
        tex.needsUpdate = true;
    };

    const configureCarcaseEdge = (tex: THREE.Texture) => {
        tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
        tex.offset.set(0, 0.5);
        tex.repeat.set(0.003, 0.003);
    };

    const material = new THREE.MeshStandardMaterial();
    bindTextureWhenReady(material, texture, configureCarcaseFace);

    const edgeMaterial = new THREE.MeshPhongMaterial();
    const edgeSource =
        edgeTexture && isTextureReady(edgeTexture) ? edgeTexture : texture;
    bindTextureWhenReady(edgeMaterial, edgeSource, configureCarcaseEdge);

    const substrateMaterial =
        carcaseSubstrateMaterial ??
        new THREE.MeshStandardMaterial({
            map: isTextureReady(texture) ? texture : getPlaceholderTexture(),
        });

    if (!carcaseSubstrateMaterial && texture && !isTextureReady(texture)) {
        bindTextureWhenReady(substrateMaterial, texture, configureCarcaseFace);
    }

    if (!showTexture) {
        material.transparent = true;
        material.opacity = 0;

        if (useSubstrateEdge) {
            substrateMaterial.transparent = true;
            substrateMaterial.opacity = 0;
        } else {
            edgeMaterial.transparent = true;
            edgeMaterial.opacity = 0;
        }
    } else {
        substrateMaterial.transparent = false;
        substrateMaterial.opacity = 1;
    }

    if (nonSupplyCarcase) {
        reduceMaterialOpacity(material);
        reduceMaterialOpacity(edgeMaterial);
    }

    const materials = [
        material,
        useSubstrateEdge ? substrateMaterial : edgeMaterial,
        edgeMaterial,
    ];

    for (let i = 0; i < geometry.groups.length; i++) {
        geometry.groups[Number(i)].materialIndex = i % 2;
    }

    return materials;
};

type MeshLabelHookContext = {
    index: number;
    shelfStyle?: number;
    label: string;
    suggestedLabelY?: number;
    suggestedLabelXOffset?: number;
    suggestedLabelX?: number;
    suggestedLabelRotationY?: number;
    // Thickness the labelled panel was extruded with. Labels sit just above
    // the panel's top face, so a shelf cut from a thicker material needs its
    // label lifted by the same amount or the text ends up inside the shelf.
    panelThickness?: number;
};

// Panels are extruded upward from their position, so a shelf cut from a
// thicker material than the carcase would sit higher than the position asked
// for. Shift it down by half the difference so the shelf stays centred on its
// intended height; carcase panels pass their own thickness and are unaffected.
const centerPanelZ = (
    positionZ: number,
    panelThickness: number,
    carcaseThickness: number
) => positionZ - (panelThickness - carcaseThickness) / 2;

export type BuildUShapedPanelShelvesParams = {
    values: PreviewFormValues;
    variables: TemplateVariable;
    shelves: AdvancedShelf[];
    carcaseThickness: number;
    // Thickness of the shelf material; falls back to the carcase thickness.
    shelfThickness?: number;
    makeUShapeMesh: (args: {
        width: number;
        depth: number;
        cutoutWidth: number;
        cutoutHeight: number;
        radius: number;
    }) => THREE.Mesh;
    lineSegmentsForMesh: (mesh: THREE.Mesh) => THREE.Object3D;
    onLabel?: (mesh: THREE.Mesh, ctx: MeshLabelHookContext) => void;
    attachEvents?: (mesh: THREE.Mesh) => void;
};

export const buildUShapedPanelShelves = ({
    values,
    variables,
    shelves,
    carcaseThickness,
    shelfThickness,
    makeUShapeMesh,
    lineSegmentsForMesh,
    onLabel,
    attachEvents,
}: BuildUShapedPanelShelvesParams) => {
    const radius = 50;
    const meshes: THREE.Mesh[] = [];
    const shelfPanelThickness = shelfThickness ?? carcaseThickness;

    if (values.cabinet_simple_shelves) return meshes;
    if (!shelves?.length) return meshes;

    for (let index = 1; index <= shelves.length; index++) {
        const selectedShelf = shelves[index - 1];
        if (!selectedShelf || selectedShelf.shelf_type !== 9) continue;
        if (variables.isReturnProduct) continue;

        const width = Number(values.cabinet_width) - carcaseThickness;
        const height = Number(values.cabinet_height);
        const depth =
            Number(values.cabinet_depth) -
            Number(selectedShelf.shelf_offset) -
            carcaseThickness;
        const cutoutWidth = width * 0.6;
        const cutoutHeight = depth * 0.5;

        const mesh = makeUShapeMesh({
            width,
            depth,
            cutoutWidth,
            cutoutHeight,
            radius,
        });

        attachEvents?.(mesh);

        const lineSegments = lineSegmentsForMesh(mesh);

        mesh.position.set(
            carcaseThickness,
            Number(selectedShelf.shelf_offset),
            centerPanelZ(
                height *
                    convertShelfPositionToPercentage(
                        selectedShelf.shelf_position,
                        height
                    ),
                shelfPanelThickness,
                carcaseThickness
            )
        );

        if (onLabel) {
            onLabel(mesh, {
                index,
                shelfStyle: selectedShelf.shelf_style,
                label: `Shelf ${index}`,
                panelThickness: shelfPanelThickness,
                suggestedLabelY: cutoutHeight + 20,
            });
        }

        mesh.add(lineSegments);
        meshes.push(mesh);
    }

    return meshes;
};

export type BuildLShapedReturnShelvesParams = {
    values: PreviewFormValues;
    variables: TemplateVariable;
    shelves: AdvancedShelf[];
    shelvesCount: number;
    carcaseThickness: number;
    // Thickness of the shelf material; falls back to the carcase thickness.
    shelfThickness?: number;
    widthOffset?: number;
    voidWidth: number;
    returnPanelWidth: number;
    isLeftReturn: boolean;
    makeLShapeMesh: (args: {
        leftWidth: number;
        leftDepth: number;
        rightDepth: number;
        radius: number;
        rightWidth: number;
    }) => THREE.Mesh;
    lineSegmentsForMesh: (mesh: THREE.Mesh) => THREE.Object3D;
    attachEvents?: (mesh: THREE.Mesh) => void;
    onLabel?: (mesh: THREE.Mesh, ctx: MeshLabelHookContext) => void;
};

export const buildLShapedReturnShelves = ({
    values,
    shelves,
    shelvesCount,
    carcaseThickness,
    shelfThickness,
    voidWidth,
    returnPanelWidth,
    isLeftReturn,
    makeLShapeMesh,
    lineSegmentsForMesh,
    attachEvents,
    onLabel,
}: BuildLShapedReturnShelvesParams) => {
    const meshes: THREE.Mesh[] = [];
    const shelfPanelThickness = shelfThickness ?? carcaseThickness;
    if (values.cabinet_simple_shelves) return meshes;
    if (!shelvesCount) return meshes;

    for (let index = 1; index <= shelvesCount; index++) {
        const selectedShelf = shelves[index - 1];
        if (!selectedShelf || selectedShelf.shelf_type !== 9) continue;

        const height = Number(values.cabinet_height);
        const leftWidth =
            Number(values.cabinet_depth) -
            carcaseThickness -
            Number(selectedShelf.shelf_offset);
        const leftDepth =
            returnPanelWidth -
            voidWidth -
            Number(selectedShelf.shelf_offset) -
            carcaseThickness;
        const rightDepth =
            Number(values.cabinet_depth) * 0.7 -
            Number(selectedShelf.shelf_offset);
        const radius = 50;
        const rightWidth = Number(values.cabinet_width) - carcaseThickness;

        const shelvePosition = (height / (shelvesCount + 1)) * index;

        const mesh = makeLShapeMesh({
            leftWidth,
            leftDepth,
            rightDepth,
            radius,
            rightWidth,
        });

        attachEvents?.(mesh);

        let positionX = carcaseThickness;
        if (!isLeftReturn) {
            positionX = rightWidth + carcaseThickness;
            mesh.scale.x = -1;
        }

        mesh.position.set(
            positionX,
            leftWidth + Number(selectedShelf.shelf_offset),
            centerPanelZ(shelvePosition, shelfPanelThickness, carcaseThickness)
        );

        mesh.scale.y = -1;

        if (onLabel) {
            onLabel(mesh, {
                index,
                shelfStyle: selectedShelf.shelf_style,
                label: `Shelf ${index}`,
                panelThickness: shelfPanelThickness,
                suggestedLabelY: rightDepth - 20,
            });
        }

        const lineSegments = lineSegmentsForMesh(mesh);
        mesh.add(lineSegments);
        meshes.push(mesh);
    }

    return meshes;
};

type AddUShapeShelfMeshParams = {
    values: PreviewFormValues;
    width: number;
    radius: number;
    positionZ: number;
    shelfOffset: number;
    positionX: number;
    carcaseThickness: number;
    shelfThickness: number;
    makeUShapeMesh: (args: {
        width: number;
        depth: number;
        cutoutWidth: number;
        cutoutHeight: number;
        radius: number;
    }) => THREE.Mesh;
    attachEvents?: (mesh: THREE.Mesh) => void;
    lineSegmentsForMesh: (mesh: THREE.Mesh) => THREE.Object3D;
    onLabel?: (mesh: THREE.Mesh, ctx: MeshLabelHookContext) => void;
    labelCtx?: MeshLabelHookContext;
};

const addUShapeShelfMesh = ({
    values,
    width,
    radius,
    positionZ,
    shelfOffset,
    positionX,
    carcaseThickness,
    shelfThickness,
    makeUShapeMesh,
    attachEvents,
    lineSegmentsForMesh,
    onLabel,
    labelCtx,
}: AddUShapeShelfMeshParams) => {
    const depth =
        Number(values.cabinet_depth) - Number(shelfOffset) - carcaseThickness;
    const cutoutWidth = width * 0.6;
    const cutoutHeight = depth * 0.5;
    const computedRadius = width < 250 ? 30 : radius;

    const mesh = makeUShapeMesh({
        width: width - carcaseThickness,
        depth,
        cutoutWidth,
        cutoutHeight,
        radius: computedRadius,
    });

    attachEvents?.(mesh);

    const lineSegments = lineSegmentsForMesh(mesh);
    mesh.position.set(
        positionX,
        Number(shelfOffset),
        centerPanelZ(positionZ, shelfThickness, carcaseThickness)
    );

    if (onLabel && labelCtx) {
        onLabel(mesh, {
            ...labelCtx,
            panelThickness: shelfThickness,
            suggestedLabelY: labelCtx.suggestedLabelY ?? cutoutHeight + 20,
        });
    }

    mesh.add(lineSegments);
    return mesh;
};

export type BuildUpperAndLowerUShapeShelvesParams = {
    values: PreviewFormValues;
    upperShelves: AdvancedUpperShelf[];
    lowerShelves: AdvancedLowerShelf[];
    carcaseThickness: number;
    // Thickness of the shelf material; falls back to the carcase thickness.
    shelfThickness?: number;
    makeUShapeMesh: AddUShapeShelfMeshParams['makeUShapeMesh'];
    lineSegmentsForMesh: (mesh: THREE.Mesh) => THREE.Object3D;
    attachEvents?: (mesh: THREE.Mesh) => void;
    onLabel?: (mesh: THREE.Mesh, ctx: MeshLabelHookContext) => void;
    labelPrefix?: {upper: string; lower: string};
};

export const buildUpperAndLowerUShapeShelves = ({
    values,
    upperShelves,
    lowerShelves,
    carcaseThickness,
    shelfThickness,
    makeUShapeMesh,
    lineSegmentsForMesh,
    attachEvents,
    onLabel,
    labelPrefix = {upper: 'Upper Shelf', lower: 'Lower Shelf'},
}: BuildUpperAndLowerUShapeShelvesParams) => {
    const upperShelvesCount = upperShelves?.length ?? 0;
    const lowerShelvesCount = lowerShelves?.length ?? 0;

    const shelvesHeightReference = {
        upper_shelves:
            Number(values.cabinet_height) -
            Number(values.cabinet_partition_height),
        lower_shelves: Number(values.cabinet_partition_height),
    };

    const upperShelfOffset = shelvesHeightReference.lower_shelves;
    const height = Number(values.cabinet_height) - upperShelfOffset;
    const radius = 50;

    const width = Number(values.cabinet_width);
    const positionX = carcaseThickness;
    const shelfPanelThickness = shelfThickness ?? carcaseThickness;

    const meshes: THREE.Mesh[] = [];

    for (let index = 1; index <= upperShelvesCount; index++) {
        const selectedShelf = upperShelves[index - 1];
        if (!selectedShelf || selectedShelf.upper_shelf_type !== 9) continue;

        const mesh = addUShapeShelfMesh({
            values,
            width,
            radius,
            makeUShapeMesh,
            positionZ:
                height *
                    convertShelfPositionToPercentage(
                        selectedShelf.upper_shelf_position,
                        height
                    ) +
                upperShelfOffset,
            shelfOffset: selectedShelf.upper_shelf_offset,
            positionX,
            carcaseThickness,
            shelfThickness: shelfPanelThickness,
            attachEvents,
            lineSegmentsForMesh,
            onLabel,
            labelCtx: {
                index,
                shelfStyle: selectedShelf.upper_shelf_style,
                label: `${labelPrefix.upper} ${index}`,
            },
        });

        meshes.push(mesh);
    }

    const height2 = upperShelfOffset;

    for (let index = 1; index <= lowerShelvesCount; index++) {
        const selectedShelf = lowerShelves[index - 1];
        if (!selectedShelf || selectedShelf.lower_shelf_type !== 9) continue;

        const mesh = addUShapeShelfMesh({
            values,
            width,
            radius,
            makeUShapeMesh,
            positionZ:
                height2 *
                convertShelfPositionToPercentage(
                    selectedShelf.lower_shelf_position,
                    height2
                ),
            shelfOffset: selectedShelf.lower_shelf_offset,
            positionX,
            carcaseThickness,
            shelfThickness: shelfPanelThickness,
            attachEvents,
            lineSegmentsForMesh,
            onLabel,
            labelCtx: {
                index,
                shelfStyle: selectedShelf.lower_shelf_style,
                label: `${labelPrefix.lower} ${index}`,
            },
        });

        meshes.push(mesh);
    }

    return meshes;
};

export type BuildUpperAndLowerLeftRightUShapeShelvesParams = {
    values: PreviewFormValues;
    carcaseThickness: number;
    // Thickness of the shelf material; falls back to the carcase thickness.
    shelfThickness?: number;
    upperShelves: AdvancedUpperShelf[];
    lowerLeftShelves: AdvancedLowerShelf[];
    lowerRightShelves: AdvancedShelf[];
    partitionWidth: number;
    makeUShapeMesh: AddUShapeShelfMeshParams['makeUShapeMesh'];
    lineSegmentsForMesh: (mesh: THREE.Mesh) => THREE.Object3D;
    attachEvents?: (mesh: THREE.Mesh) => void;
    onLabel?: (mesh: THREE.Mesh, ctx: MeshLabelHookContext) => void;
};

export const buildUpperAndLowerLeftRightUShapeShelves = ({
    values,
    carcaseThickness,
    shelfThickness,
    upperShelves,
    lowerLeftShelves,
    lowerRightShelves,
    partitionWidth,
    makeUShapeMesh,
    lineSegmentsForMesh,
    attachEvents,
    onLabel,
}: BuildUpperAndLowerLeftRightUShapeShelvesParams) => {
    const upperShelvesCount = upperShelves?.length ?? 0;
    const lowerLeftShelvesCount = lowerLeftShelves?.length ?? 0;
    const lowerRightShelvesCount = lowerRightShelves?.length ?? 0;

    const shelvesHeightReference = {
        upper_shelves:
            Number(values.cabinet_height) -
            Number(values.cabinet_partition_height),
        lower_shelves: Number(values.cabinet_partition_height),
    };

    const upperShelfOffset = shelvesHeightReference.lower_shelves;
    const height = Number(values.cabinet_height) - upperShelfOffset;

    const radius = 50;
    const width = Number(values.cabinet_width);
    const positionX = carcaseThickness;
    const shelfPanelThickness = shelfThickness ?? carcaseThickness;

    const meshes: THREE.Mesh[] = [];

    for (let index = 1; index <= upperShelvesCount; index++) {
        const selectedShelf = upperShelves[index - 1];
        if (!selectedShelf || selectedShelf.upper_shelf_type !== 9) continue;

        meshes.push(
            addUShapeShelfMesh({
                values,
                width,
                radius,
                makeUShapeMesh,
                positionZ:
                    height *
                        convertShelfPositionToPercentage(
                            selectedShelf.upper_shelf_position,
                            height
                        ) +
                    upperShelfOffset,
                shelfOffset: selectedShelf.upper_shelf_offset,
                positionX,
                carcaseThickness,
                shelfThickness: shelfPanelThickness,
                attachEvents,
                lineSegmentsForMesh,
                onLabel,
                labelCtx: {
                    index,
                    shelfStyle: selectedShelf.upper_shelf_style,
                    label: `Upper Shelf ${index}`,
                },
            })
        );
    }

    const height2 = upperShelfOffset;

    for (let index = 1; index <= lowerLeftShelvesCount; index++) {
        const selectedShelf = lowerLeftShelves[index - 1];
        if (!selectedShelf || selectedShelf.lower_shelf_type !== 9) continue;

        meshes.push(
            addUShapeShelfMesh({
                values,
                width: partitionWidth,
                radius,
                makeUShapeMesh,
                positionZ:
                    height2 *
                    convertShelfPositionToPercentage(
                        selectedShelf.lower_shelf_position,
                        height2
                    ),
                shelfOffset: selectedShelf.lower_shelf_offset,
                positionX,
                carcaseThickness,
                shelfThickness: shelfPanelThickness,
                attachEvents,
                lineSegmentsForMesh,
                onLabel,
                labelCtx: {
                    index,
                    shelfStyle: selectedShelf.lower_shelf_style,
                    label: `BL ${index}`,
                },
            })
        );
    }

    for (let index = 1; index <= lowerRightShelvesCount; index++) {
        const selectedShelf = lowerRightShelves[index - 1];
        if (!selectedShelf || selectedShelf.shelf_type !== 9) continue;

        meshes.push(
            addUShapeShelfMesh({
                values,
                width: width - partitionWidth,
                radius,
                makeUShapeMesh,
                positionZ:
                    height2 *
                    convertShelfPositionToPercentage(
                        selectedShelf.shelf_position,
                        height2
                    ),
                shelfOffset: selectedShelf.shelf_offset,
                positionX: positionX + partitionWidth,
                carcaseThickness,
                shelfThickness: shelfPanelThickness,
                attachEvents,
                lineSegmentsForMesh,
                onLabel,
                labelCtx: {
                    index,
                    shelfStyle: selectedShelf.shelf_style,
                    label: `BR ${index}`,
                },
            })
        );
    }

    return meshes;
};

export type BuildShelvesWithOvenVentsParams = {
    values: PreviewFormValues;
    variables: TemplateVariable;
    carcaseThickness: number;
    // Thickness of the shelf material. Only the vent shelf is extruded with
    // it — the cabinet's own bottom panel keeps the carcase thickness.
    shelfThickness?: number;
    makeVentShelfMesh: (args: {
        width: number;
        depth: number;
        thickness: number;
    }) => THREE.Mesh;
    lineSegmentsForMesh: (mesh: THREE.Mesh) => THREE.Object3D;
    attachEvents?: (mesh: THREE.Mesh) => void;
};

export const buildShelvesWithOvenVents = ({
    values,
    variables,
    carcaseThickness,
    shelfThickness,
    makeVentShelfMesh,
    lineSegmentsForMesh,
    attachEvents,
}: BuildShelvesWithOvenVentsParams) => {
    const ovenOpeningHeight = Number(values.oven_opening_height);
    const height = Number(values.cabinet_height);
    const width = Number(values.cabinet_width) - carcaseThickness;
    const depth = Number(values.cabinet_depth) - carcaseThickness;
    let ventLocation = (height - ovenOpeningHeight) / 2 - carcaseThickness;

    if (
        Boolean(values.drawers) &&
        values.drawers?.length > 0 &&
        !!values.drawers[0].drawer_face_height
    ) {
        ventLocation = height - ovenOpeningHeight - carcaseThickness;
    }

    if (variables?.isWallOvenProduct) {
        ventLocation =
            Number(values.cabinet_total_drawer_height) - carcaseThickness;
    }

    // The panel at z 0 is the cabinet bottom, so it keeps the carcase
    // thickness; only the vent shelf follows the shelf material.
    const vents = [
        {locationZ: 0, thickness: carcaseThickness},
        {
            locationZ: ventLocation,
            thickness: shelfThickness ?? carcaseThickness,
        },
    ];
    const meshes: THREE.Mesh[] = [];

    vents.forEach((vent) => {
        const mesh = makeVentShelfMesh({
            width,
            depth,
            thickness: vent.thickness,
        });
        attachEvents?.(mesh);

        mesh.position.z = centerPanelZ(
            vent.locationZ,
            vent.thickness,
            carcaseThickness
        );
        mesh.position.x = carcaseThickness;

        const lineSegments = lineSegmentsForMesh(mesh);
        mesh.add(lineSegments);
        meshes.push(mesh);
    });

    return meshes;
};

export type BuildShelvesWithRangehoodCutoutParams = {
    values: PreviewFormValues;
    carcaseThickness: number;
    // Thickness of the shelf material. Only the shelves are extruded with it —
    // the cabinet's own bottom/top panels always keep the carcase thickness.
    shelfThickness?: number;
    shelvesCount: number;
    advancedShelves: AdvancedShelf[] | undefined;
    makeRangehoodCutoutMesh: (args: {
        width: number;
        depth: number;
        thickness: number;
    }) => THREE.Mesh;
    lineSegmentsForMesh: (mesh: THREE.Mesh) => THREE.Object3D;
    attachEvents?: (mesh: THREE.Mesh) => void;
    onLabel?: (mesh: THREE.Mesh, ctx: MeshLabelHookContext) => void;
};

export const buildShelvesWithRangehoodCutout = ({
    values,
    carcaseThickness,
    shelfThickness,
    shelvesCount,
    advancedShelves,
    makeRangehoodCutoutMesh,
    lineSegmentsForMesh,
    attachEvents,
    onLabel,
}: BuildShelvesWithRangehoodCutoutParams) => {
    const rangehoodOpeningHeight = Number(values.rangehood_opening_height);
    const height = Number(values.cabinet_height) - rangehoodOpeningHeight;
    const width = Number(values.cabinet_width) - carcaseThickness;
    const depth = Number(values.cabinet_depth) - carcaseThickness;
    const shelfPanelThickness = shelfThickness ?? carcaseThickness;

    const meshes: THREE.Mesh[] = [];

    for (let index = 0; index < shelvesCount + 2; index++) {
        let shelvePosition =
            (height / (shelvesCount + 1)) * index + rangehoodOpeningHeight;

        const isBoundary = index === 0 || index === shelvesCount + 1;
        const isTop = index === shelvesCount + 1;

        if (!values.cabinet_simple_shelves) {
            const selectedShelf = advancedShelves?.[index - 1];
            if (selectedShelf && !isBoundary) {
                shelvePosition =
                    height *
                        convertShelfPositionToPercentage(
                            selectedShelf.shelf_position,
                            height
                        ) +
                    rangehoodOpeningHeight;
            }
        }

        // The boundary panels are the cabinet's bottom and top, so they keep
        // the carcase thickness.
        const panelThickness = isBoundary
            ? carcaseThickness
            : shelfPanelThickness;
        const mesh = makeRangehoodCutoutMesh({
            width,
            depth,
            thickness: panelThickness,
        });
        attachEvents?.(mesh);

        mesh.position.set(
            carcaseThickness,
            0,
            centerPanelZ(
                shelvePosition - (isTop ? carcaseThickness : 0),
                panelThickness,
                carcaseThickness
            )
        );

        if (onLabel && !isBoundary) {
            const selectedShelf = advancedShelves?.[index - 1];
            onLabel(mesh, {
                index,
                shelfStyle: selectedShelf?.shelf_style,
                label: `Shelf ${index}`,
                panelThickness,
                suggestedLabelY: 20,
            });
        }

        const lineSegments = lineSegmentsForMesh(mesh);
        mesh.add(lineSegments);
        meshes.push(mesh);
    }

    return meshes;
};

export type BuildClipPanelParams = {
    values: PreviewFormValues;
    variables: TemplateVariable;
    shelvesCount: number;
    advancedShelves: AdvancedShelf[] | undefined;
    exteriorThickness: number;
    makeCornerShapeMesh: (args: {
        leftWidth: number;
        leftDepth: number;
        rightDepth: number;
        rightWidth: number;
    }) => THREE.Mesh;
    lineSegmentsForMesh: (mesh: THREE.Mesh) => THREE.Object3D;
    attachEvents?: (mesh: THREE.Mesh) => void;
    onLabel?: (mesh: THREE.Mesh, ctx: MeshLabelHookContext) => void;
};

export const buildClipPanel = ({
    values,
    variables,
    shelvesCount,
    advancedShelves,
    exteriorThickness,
    makeCornerShapeMesh,
    lineSegmentsForMesh,
    attachEvents,
    onLabel,
}: BuildClipPanelParams) => {
    const height = Number(values.cabinet_height);
    const depth = Number(values.cabinet_depth);
    const width = Number(values.cabinet_width);
    const clipSize = Math.min(depth, width) * 0.5;

    const meshes: THREE.Mesh[] = [];

    for (let index = 0; index <= shelvesCount + 1; index++) {
        let rightWidth = width - exteriorThickness;
        let leftWidth = depth - exteriorThickness;
        let shelvePosition = (height / (shelvesCount + 1)) * index;

        const shouldApplyOffset = index !== 0 && index !== shelvesCount + 1;
        const selectedShelf = advancedShelves?.[index - 1];
        let offset = 0;

        let rightDepth: number;
        let leftDepth: number;
        if (width > depth) {
            leftDepth = rightWidth - clipSize;
            rightDepth = clipSize;
        } else {
            leftDepth = clipSize;
            rightDepth = leftWidth - clipSize;
        }

        if (
            !values.cabinet_simple_shelves &&
            selectedShelf &&
            index > 0 &&
            index <= shelvesCount
        ) {
            shelvePosition =
                height *
                convertShelfPositionToPercentage(
                    selectedShelf.shelf_position,
                    height
                );

            rightWidth =
                width -
                exteriorThickness -
                (shouldApplyOffset ? selectedShelf.shelf_offset : 0);
            leftWidth =
                depth -
                exteriorThickness -
                (shouldApplyOffset ? selectedShelf.shelf_offset : 0);

            offset = selectedShelf.shelf_offset;
        } else if (
            values.cabinet_simple_shelves &&
            index > 0 &&
            index <= shelvesCount &&
            shouldApplyOffset
        ) {
            const setback = selectedShelf
                ? selectedShelf.shelf_offset
                : Number(values.shelf_set_back ?? 0);
            rightWidth -= setback;
            leftWidth -= setback;
            offset = setback;
        }

        if (index === shelvesCount + 1) {
            shelvePosition = height - exteriorThickness;
        }

        const mesh = makeCornerShapeMesh({
            leftWidth,
            leftDepth: leftDepth - offset,
            rightDepth: rightDepth - offset,
            rightWidth,
        });

        attachEvents?.(mesh);

        mesh.position.set(
            !variables?.isLeftClip ? exteriorThickness : width,
            leftWidth + (shouldApplyOffset ? offset : 0),
            shelvePosition
        );

        mesh.scale.y = -1;
        if (variables?.isLeftClip) mesh.scale.x = -1;

        if (onLabel && shouldApplyOffset) {
            onLabel(mesh, {
                index,
                shelfStyle: selectedShelf?.shelf_style,
                label: `Shelf ${index}`,
                suggestedLabelY: variables?.isLeftClip ? depth / 2 : rightDepth,
                suggestedLabelXOffset: variables?.isLeftClip
                    ? 0.25 * depth - 350
                    : -(rightWidth - leftDepth) / 2,
                suggestedLabelRotationY: variables?.isLeftClip ? Math.PI : 0,
            });
        }

        const lineSegments = lineSegmentsForMesh(mesh);
        mesh.add(lineSegments);
        meshes.push(mesh);
    }

    return meshes;
};

export type BuildRadiusPanelParams = {
    values: PreviewFormValues;
    variables: TemplateVariable;
    shelvesCount: number;
    advancedShelves: AdvancedShelf[] | undefined;
    exteriorThickness: number;
    makeRadiusMesh: (args: {
        width: number;
        depth: number;
        offset: number;
    }) => THREE.Mesh;
    lineSegmentsForMesh: (mesh: THREE.Mesh) => THREE.Object3D;
    attachEvents?: (mesh: THREE.Mesh) => void;
    onLabel?: (mesh: THREE.Mesh, ctx: MeshLabelHookContext) => void;
};

export const buildRadiusPanel = ({
    values,
    variables,
    shelvesCount,
    advancedShelves,
    exteriorThickness,
    makeRadiusMesh,
    lineSegmentsForMesh,
    attachEvents,
    onLabel,
}: BuildRadiusPanelParams) => {
    const height = Number(values.cabinet_height);
    const depth = Number(values.cabinet_depth) - exteriorThickness;
    const width = Number(values.cabinet_width) - exteriorThickness;

    const meshes: THREE.Mesh[] = [];

    for (let index = 0; index <= shelvesCount + 1; index++) {
        let shelvePosition = (height / (shelvesCount + 1)) * index;
        const shouldApplyOffset = index !== 0 && index !== shelvesCount + 1;
        const selectedShelf = advancedShelves?.[index - 1];
        let offset = 0;

        if (
            !values.cabinet_simple_shelves &&
            selectedShelf &&
            index > 0 &&
            index <= shelvesCount
        ) {
            shelvePosition =
                height *
                convertShelfPositionToPercentage(
                    selectedShelf.shelf_position,
                    height
                );
            offset = selectedShelf.shelf_offset;
        } else if (
            values.cabinet_simple_shelves &&
            index > 0 &&
            index <= shelvesCount
        ) {
            offset = selectedShelf
                ? selectedShelf.shelf_offset
                : Number(values.shelf_set_back ?? 0);
        }

        if (index === shelvesCount + 1)
            shelvePosition = height - exteriorThickness;

        const mesh = makeRadiusMesh({width, depth, offset});
        attachEvents?.(mesh);

        mesh.position.set(
            !variables?.isLeftRadius
                ? exteriorThickness
                : width + exteriorThickness,
            depth,
            shelvePosition
        );

        if (variables?.isLeftRadius) {
            mesh.rotation.z = Math.PI / 2;
            mesh.scale.x = -1;
        } else {
            mesh.scale.y = -1;
        }

        if (onLabel && shouldApplyOffset) {
            onLabel(mesh, {
                index,
                shelfStyle: selectedShelf?.shelf_style,
                label: `Shelf ${index}`,
                suggestedLabelY: depth / 2,
            });
        }

        const lineSegments = lineSegmentsForMesh(mesh);
        mesh.add(lineSegments);
        meshes.push(mesh);
    }

    return meshes;
};

export type BuildCornerShapedPanelParams = {
    values: PreviewFormValues;
    variables: TemplateVariable;
    shelvesCount: number;
    // Pantry products carry upper_shelves/lower_shelves instead of shelves, so
    // this can arrive undefined.
    advancedShelves: AdvancedShelf[] | undefined;
    carcaseThickness: number;
    // Thickness of the shelf material. Only the shelves are extruded with it —
    // the cabinet's own bottom/top panels always keep the carcase thickness.
    shelfThickness?: number;
    isFfRailOnFlat: boolean;
    isFfSolidTop: boolean;
    leftWidth: number;
    rightWidth: number;
    makeFfRailOnFlatMesh: () => THREE.Mesh;
    makeLShapeShelfMesh: (args: {
        leftDepth: number;
        rightDepth: number;
        rightWidth: number;
        thickness: number;
    }) => THREE.Mesh;
    makeCornerShelfMesh: (args: {
        leftDepth: number;
        rightDepth: number;
        rightWidth: number;
        shouldApplyOffset: boolean;
        thickness: number;
    }) => THREE.Mesh;
    lineSegmentsForMesh: (mesh: THREE.Mesh) => THREE.Object3D;
    attachEvents?: (mesh: THREE.Mesh) => void;
    onLabel?: (mesh: THREE.Mesh, ctx: MeshLabelHookContext) => void;
};

export const buildCornerShapedPanel = ({
    values,
    shelvesCount,
    advancedShelves,
    carcaseThickness,
    shelfThickness,
    isFfRailOnFlat,
    isFfSolidTop,
    leftWidth,
    rightWidth,
    makeFfRailOnFlatMesh,
    makeLShapeShelfMesh,
    makeCornerShelfMesh,
    lineSegmentsForMesh,
    attachEvents,
    onLabel,
}: BuildCornerShapedPanelParams) => {
    const height = Number(values.cabinet_height);
    const shelfPanelThickness = shelfThickness ?? carcaseThickness;
    const meshes: THREE.Mesh[] = [];

    if (isFfRailOnFlat) {
        const mesh = makeFfRailOnFlatMesh();
        attachEvents?.(mesh);
        mesh.position.set(
            carcaseThickness,
            -carcaseThickness,
            height - carcaseThickness
        );
        mesh.add(lineSegmentsForMesh(mesh));
        meshes.push(mesh);
    }

    const useStoredPositions =
        !values.cabinet_simple_shelves &&
        hasUsableShelfPositions(advancedShelves, shelvesCount, height);

    for (
        let index = 0;
        index <= shelvesCount + (isFfSolidTop ? 1 : 0);
        index++
    ) {
        let shelvePosition = (height / (shelvesCount + 1)) * index;
        let setback = 0;

        const isSolidTopSelected = index === shelvesCount + 1;
        const shouldApplyOffset = index !== 0 && index !== shelvesCount + 1;

        const selectedShelf = advancedShelves?.[index - 1];
        const storedPosition =
            selectedShelf && useStoredPositions
                ? convertShelfPositionToPercentage(
                      selectedShelf.shelf_position,
                      height
                  )
                : 0;
        if (selectedShelf && storedPosition > 0 && storedPosition < 1) {
            shelvePosition = height * storedPosition;

            if (shouldApplyOffset)
                setback = resolveShelfSetBack(
                    values,
                    selectedShelf.shelf_offset
                );
        } else if (values.cabinet_simple_shelves && shouldApplyOffset) {
            setback = resolveShelfSetBack(values);
        }

        const rightDepth = Number(values.cabinet_right_depth) - setback;
        const leftDepth =
            Number(values.cabinet_left_depth) - carcaseThickness - setback;

        if (index === shelvesCount + 1) shelvePosition = height;

        const panelThickness = shouldApplyOffset
            ? shelfPanelThickness
            : carcaseThickness;
        const isLsShapeShelf =
            !values.cabinet_simple_shelves &&
            shouldApplyOffset &&
            isLsShapeShelfInsert(selectedShelf);
        const mesh = isLsShapeShelf
            ? makeLShapeShelfMesh({
                  leftDepth,
                  rightDepth,
                  rightWidth,
                  thickness: panelThickness,
              })
            : makeCornerShelfMesh({
                  leftDepth,
                  rightDepth,
                  rightWidth,
                  shouldApplyOffset,
                  thickness: panelThickness,
              });

        attachEvents?.(mesh);

        mesh.position.set(
            carcaseThickness,
            leftWidth - 2,
            centerPanelZ(
                shelvePosition -
                    (isSolidTopSelected ? carcaseThickness : 0) +
                    1,
                panelThickness,
                carcaseThickness
            )
        );
        mesh.scale.y = -1;

        if (onLabel && shouldApplyOffset) {
            onLabel(mesh, {
                index,
                shelfStyle: selectedShelf?.shelf_style,
                label: `Shelf ${index}`,
                panelThickness,
                suggestedLabelY: isLsShapeShelf
                    ? rightDepth
                    : (leftWidth + rightDepth) / 2,
                suggestedLabelXOffset: isLsShapeShelf
                    ? -(rightWidth - leftDepth) / 2
                    : undefined,
                suggestedLabelX: isLsShapeShelf
                    ? undefined
                    : (leftDepth + rightWidth) / 2,
                suggestedLabelRotationY: isLsShapeShelf
                    ? 0
                    : Math.atan2(
                          rightDepth - leftWidth,
                          rightWidth - leftDepth
                      ),
            });
        }

        mesh.add(lineSegmentsForMesh(mesh));
        meshes.push(mesh);
    }

    return meshes;
};

export type BuildLShapedPanelForPantryParams = {
    values: PreviewFormValues;
    variables: TemplateVariable;
    carcaseThickness: number;
    // Thickness of the shelf material. Only the shelves are extruded with it —
    // the cabinet's own bottom/top/partition panels always keep the carcase
    // thickness.
    shelfThickness?: number;
    upperShelves: AdvancedUpperShelf[];
    lowerShelves: AdvancedLowerShelf[];
    upperShelvesCount: number;
    lowerShelvesCount: number;
    upperShelfOffset: number;
    height: number;
    leftWidth: number;
    rightWidth: number;
    radius: number;
    makeLShapeMesh: (args: {
        leftDepth: number;
        rightDepth: number;
        thickness: number;
    }) => THREE.Mesh;
    lineSegmentsForMesh: (mesh: THREE.Mesh) => THREE.Object3D;
    attachEvents?: (mesh: THREE.Mesh) => void;
    onLabel?: (mesh: THREE.Mesh, ctx: MeshLabelHookContext) => void;
};

export const buildLShapedPanelForPantry = ({
    values,
    variables,
    carcaseThickness,
    shelfThickness,
    upperShelves,
    lowerShelves,
    upperShelvesCount,
    lowerShelvesCount,
    upperShelfOffset,
    height,
    leftWidth,
    rightWidth,
    makeLShapeMesh,
    lineSegmentsForMesh,
    attachEvents,
    onLabel,
}: BuildLShapedPanelForPantryParams) => {
    const meshes: THREE.Mesh[] = [];
    const shelfPanelThickness = shelfThickness ?? carcaseThickness;

    for (let index = 0; index <= upperShelvesCount + 1; index++) {
        const selectedShelf = upperShelves[index - 1];
        const leftDepthValue =
            Number(values.cabinet_left_depth) - carcaseThickness - 1;

        let rightDepth = Number(values.cabinet_right_depth);
        let leftDepth = leftDepthValue;
        let shelvePosition = Number(values.cabinet_height) - carcaseThickness;
        const shouldApplyOffset =
            index !== 0 && index !== upperShelvesCount + 1;

        if (selectedShelf && Number(selectedShelf.upper_shelf_type) === 14) {
            const offset = clampShelfOffset(
                shouldApplyOffset,
                selectedShelf.upper_shelf_offset
            );
            rightDepth = Number(values.cabinet_right_depth) - offset;
            leftDepth = leftDepthValue - offset;

            shelvePosition =
                height *
                    convertShelfPositionToPercentage(
                        selectedShelf.upper_shelf_position,
                        height
                    ) +
                upperShelfOffset;
        }

        const isHorizontalPartition = index === upperShelvesCount + 1;
        if (isHorizontalPartition) shelvePosition = upperShelfOffset;

        // index 0 (cabinet top) and the horizontal partition are carcase
        // panels, so they keep the carcase thickness.
        const panelThickness = shouldApplyOffset
            ? shelfPanelThickness
            : carcaseThickness;
        const mesh = makeLShapeMesh({
            leftDepth,
            rightDepth,
            thickness: panelThickness,
        });
        attachEvents?.(mesh);

        mesh.position.set(
            carcaseThickness,
            leftWidth - 2,
            centerPanelZ(shelvePosition, panelThickness, carcaseThickness)
        );
        mesh.scale.y = -1;

        if (onLabel && shouldApplyOffset && selectedShelf) {
            onLabel(mesh, {
                index,
                shelfStyle: selectedShelf.upper_shelf_style,
                label: `Upper Shelf ${index}`,
                panelThickness,
                suggestedLabelY: rightDepth,
                suggestedLabelXOffset: -(rightWidth - leftDepth) / 2,
            });
        }

        mesh.add(lineSegmentsForMesh(mesh));
        meshes.push(mesh);
    }

    if (variables?.is2Part) {
        const leftDepth =
            Number(values.cabinet_left_depth) - carcaseThickness - 1;
        const rightDepth = Number(values.cabinet_right_depth);

        const bottomPartitionMesh = makeLShapeMesh({
            leftDepth,
            rightDepth,
            thickness: carcaseThickness,
        });
        attachEvents?.(bottomPartitionMesh);

        bottomPartitionMesh.position.set(
            carcaseThickness,
            leftWidth - 2,
            upperShelfOffset - carcaseThickness
        );
        bottomPartitionMesh.scale.y = -1;

        bottomPartitionMesh.add(lineSegmentsForMesh(bottomPartitionMesh));
        meshes.push(bottomPartitionMesh);
    }

    const height2 = upperShelfOffset;

    for (let index = 0; index <= lowerShelvesCount; index++) {
        const selectedShelf = lowerShelves[index - 1];
        const leftDepthValue =
            Number(values.cabinet_left_depth) - carcaseThickness - 1;

        let rightDepth = Number(values.cabinet_right_depth);
        let leftDepth = leftDepthValue;
        let shelvePosition = 0;
        const shouldApplyOffset = index !== 0;

        if (selectedShelf && Number(selectedShelf.lower_shelf_type) === 14) {
            const offset = clampShelfOffset(
                shouldApplyOffset,
                selectedShelf.lower_shelf_offset
            );
            rightDepth = Number(values.cabinet_right_depth) - offset;
            leftDepth = leftDepthValue - offset;

            shelvePosition =
                height2 *
                convertShelfPositionToPercentage(
                    selectedShelf.lower_shelf_position,
                    height2
                );
        }

        // index 0 is the cabinet bottom: a carcase panel.
        const panelThickness = shouldApplyOffset
            ? shelfPanelThickness
            : carcaseThickness;
        const mesh = makeLShapeMesh({
            leftDepth,
            rightDepth,
            thickness: panelThickness,
        });
        attachEvents?.(mesh);

        mesh.position.set(
            carcaseThickness,
            leftWidth - 2,
            centerPanelZ(shelvePosition, panelThickness, carcaseThickness)
        );
        mesh.scale.y = -1;

        if (onLabel && shouldApplyOffset && selectedShelf) {
            onLabel(mesh, {
                index,
                shelfStyle: selectedShelf.lower_shelf_style,
                label: `Lower Shelf ${index}`,
                panelThickness,
                suggestedLabelY: rightDepth,
                suggestedLabelXOffset: -(rightWidth - leftDepth) / 2,
            });
        }

        mesh.add(lineSegmentsForMesh(mesh));
        meshes.push(mesh);
    }

    return meshes;
};

type CornerPantryShelfConfig<TShelf> = {
    shelves: TShelf[];
    shelvesCount: number;
    sectionHeight: number;
    sectionOffsetZ: number;
    startIndex: number;
    endExtra: number;
    index0Z: number;
    partitionLabel?: string;
    getShelfPosition: (shelf: TShelf) => number;
    getShelfOffset: (shelf: TShelf) => number;
    getShelfStyle?: (shelf: TShelf) => number | null | undefined;
    isCornerSolidTopSelected?: (index: number) => boolean;
    shouldApplyOffset: (
        index: number,
        shelvesCount: number,
        endExtra: number
    ) => boolean;
    panelEncloserPositionZ: number;
};

export type BuildCornerShapePanelForPantryParams = {
    values: PreviewFormValues;
    variables: TemplateVariable;
    carcaseThickness: number;
    // Thickness of the shelf material. Only the shelves are extruded with it —
    // the cabinet's own bottom/top/partition panels always keep the carcase
    // thickness.
    shelfThickness?: number;
    leftWidth: number;
    rightWidth: number;
    widthOffsetLShape: number;
    upperShelves: AdvancedUpperShelf[];
    lowerShelves: AdvancedLowerShelf[];
    upperShelvesCount: number;
    lowerShelvesCount: number;
    upperShelfOffset: number;
    height: number;
    lowerStartIndex: number;
    lowerEndExtra: number;
    makeLShapeMesh: (args: {
        leftDepth: number;
        rightDepth: number;
        rightWidth: number;
        thickness: number;
    }) => THREE.Mesh;
    makeCornerShapeMesh: (args: {
        leftDepth: number;
        rightDepth: number;
        rightWidth: number;
        thickness: number;
    }) => THREE.Mesh;
    lineSegmentsForMesh: (mesh: THREE.Mesh) => THREE.Object3D;
    attachEvents?: (mesh: THREE.Mesh) => void;
    onLabel?: (mesh: THREE.Mesh, ctx: MeshLabelHookContext) => void;
};

export const buildCornerShapePanelForPantry = ({
    values,
    carcaseThickness,
    shelfThickness,
    leftWidth,
    rightWidth,
    upperShelves,
    lowerShelves,
    upperShelvesCount,
    lowerShelvesCount,
    upperShelfOffset,
    height,
    lowerStartIndex,
    lowerEndExtra,
    makeLShapeMesh,
    makeCornerShapeMesh,
    lineSegmentsForMesh,
    attachEvents,
    onLabel,
}: BuildCornerShapePanelForPantryParams) => {
    const meshes: THREE.Mesh[] = [];
    const shelfPanelThickness = shelfThickness ?? carcaseThickness;
    const positionX = carcaseThickness;
    const positionY = leftWidth - 2;

    // makeLShapeMesh omits the carc*2 + 2 widening that makeCornerShapeMesh
    // applies, leaving these shelves ~carc*2 short of the right wall. Match it.
    const lShapeRightWidth = rightWidth + carcaseThickness * 2 + 2;

    const buildSection = <TShelf>({
        shelves,
        shelvesCount,
        sectionHeight,
        sectionOffsetZ,
        startIndex,
        endExtra,
        index0Z,
        partitionLabel,
        getShelfPosition,
        getShelfOffset,
        getShelfStyle,
        isCornerSolidTopSelected,
        shouldApplyOffset,
        panelEncloserPositionZ,
    }: CornerPantryShelfConfig<TShelf>) => {
        for (
            let index = startIndex;
            index <= shelvesCount + endExtra;
            index++
        ) {
            const selectedShelf = shelves[index - 1];
            const shouldOffset = shouldApplyOffset(
                index,
                shelvesCount,
                endExtra
            );
            const isSolidTopSelected =
                isCornerSolidTopSelected?.(index) ?? false;
            let shelvePosition = sectionHeight;
            let rightDepth = Number(values.cabinet_right_depth);
            let leftDepth =
                Number(values.cabinet_left_depth) - carcaseThickness;

            if (selectedShelf) {
                shelvePosition =
                    sectionHeight *
                        convertShelfPositionToPercentage(
                            getShelfPosition(selectedShelf),
                            sectionHeight
                        ) +
                    sectionOffsetZ;

                const offset = clampShelfOffset(
                    shouldOffset,
                    getShelfOffset(selectedShelf)
                );
                rightDepth = Number(values.cabinet_right_depth) - offset;
                leftDepth =
                    Number(values.cabinet_left_depth) -
                    carcaseThickness -
                    offset;
            }

            if (index === 0) {
                shelvePosition = index0Z;
            }

            const isPanelEncloser = index === shelvesCount + 1;
            // Only real shelves take the shelf material's thickness; the
            // partitions and the top/bottom enclosers are carcase panels.
            const panelThickness = shouldOffset
                ? shelfPanelThickness
                : carcaseThickness;
            const mesh =
                (shouldOffset || index === 0 || partitionLabel) &&
                !isPanelEncloser
                    ? makeLShapeMesh({
                          leftDepth: leftDepth - 1,
                          rightDepth,
                          rightWidth: lShapeRightWidth,
                          thickness: panelThickness,
                      })
                    : makeCornerShapeMesh({
                          leftDepth: leftDepth - 1,
                          rightDepth,
                          rightWidth,
                          thickness: panelThickness,
                      });

            attachEvents?.(mesh);

            mesh.position.set(
                positionX,
                positionY,
                centerPanelZ(
                    shelvePosition -
                        (isSolidTopSelected ? carcaseThickness : 0) +
                        1,
                    panelThickness,
                    carcaseThickness
                )
            );
            mesh.scale.y = -1;

            if (isPanelEncloser) mesh.position.z = panelEncloserPositionZ;

            if (onLabel) {
                if (shouldOffset && selectedShelf) {
                    onLabel(mesh, {
                        index,
                        shelfStyle: getShelfStyle?.(selectedShelf),
                        label: `Shelf ${index}`,
                        panelThickness,
                        suggestedLabelY: rightDepth,
                        suggestedLabelXOffset: -(rightWidth - leftDepth) / 2,
                    });
                } else if (index === 0 && partitionLabel) {
                    onLabel(mesh, {
                        index,
                        label: partitionLabel,
                        suggestedLabelY: rightDepth,
                        suggestedLabelXOffset: -(rightWidth - leftDepth) / 2,
                    });
                }
            }

            mesh.add(lineSegmentsForMesh(mesh));
            meshes.push(mesh);
        }
    };

    buildSection<AdvancedUpperShelf>({
        shelves: upperShelves,
        shelvesCount: upperShelvesCount,
        sectionHeight: height,
        sectionOffsetZ: upperShelfOffset,
        startIndex: 0,
        endExtra: 1,
        index0Z: upperShelfOffset,
        partitionLabel: 'Horizontal Partition',
        getShelfPosition: (s) => s.upper_shelf_position,
        getShelfOffset: (s) => s.upper_shelf_offset,
        getShelfStyle: (s) => s.upper_shelf_style,
        isCornerSolidTopSelected: (index) => index === upperShelvesCount + 1,
        shouldApplyOffset: (index, count, endExtra) =>
            index !== 0 && index !== count + endExtra,
        panelEncloserPositionZ:
            Number(values.cabinet_height) - carcaseThickness,
    });

    const height2 = upperShelfOffset;
    buildSection<AdvancedLowerShelf>({
        shelves: lowerShelves,
        shelvesCount: lowerShelvesCount,
        sectionHeight: height2,
        sectionOffsetZ: 0,
        startIndex: lowerStartIndex,
        endExtra: lowerEndExtra,
        index0Z: height2 - carcaseThickness,
        getShelfPosition: (s) => s.lower_shelf_position,
        getShelfOffset: (s) => s.lower_shelf_offset,
        getShelfStyle: (s) => s.lower_shelf_style,
        shouldApplyOffset: (index, count) => index !== 0 && index !== count + 1,
        panelEncloserPositionZ: 0,
    });

    return meshes;
};

export type BuildCornerShapePanelFor3PartPantryParams = {
    values: PreviewFormValues;
    variables: TemplateVariable;
    carcaseThickness: number;
    // Thickness of the shelf material. Only the shelves are extruded with it —
    // the cabinet's own bottom/top/partition panels always keep the carcase
    // thickness.
    shelfThickness?: number;
    leftWidth: number;
    rightWidth: number;
    widthOffsetLShape: number;
    upperShelves: AdvancedUpperShelf[];
    middleShelves: AdvancedMiddleShelf[];
    lowerShelves: AdvancedLowerShelf[];
    upperShelvesCount: number;
    middleShelvesCount: number;
    lowerShelvesCount: number;
    upperShelfOffset: number;
    lowerShelfOffset: number;
    heightUpper: number;
    heightMiddle: number;
    heightLower: number;
    lowerEndExtra: number;
    makeLShapeMesh: (args: {
        leftDepth: number;
        rightDepth: number;
        rightWidth: number;
        thickness: number;
    }) => THREE.Mesh;
    makeCornerShapeMesh: (args: {
        leftDepth: number;
        rightDepth: number;
        rightWidth: number;
        thickness: number;
    }) => THREE.Mesh;
    lineSegmentsForMesh: (mesh: THREE.Mesh) => THREE.Object3D;
    attachEvents?: (mesh: THREE.Mesh) => void;
    onLabel?: (mesh: THREE.Mesh, ctx: MeshLabelHookContext) => void;
};

export const buildCornerShapePanelFor3PartPantry = ({
    values,
    carcaseThickness,
    shelfThickness,
    leftWidth,
    rightWidth,
    upperShelves,
    middleShelves,
    lowerShelves,
    upperShelvesCount,
    middleShelvesCount,
    lowerShelvesCount,
    upperShelfOffset,
    lowerShelfOffset,
    heightUpper,
    heightMiddle,
    heightLower,
    lowerEndExtra,
    makeLShapeMesh,
    makeCornerShapeMesh,
    lineSegmentsForMesh,
    attachEvents,
    onLabel,
}: BuildCornerShapePanelFor3PartPantryParams) => {
    const meshes: THREE.Mesh[] = [];
    const shelfPanelThickness = shelfThickness ?? carcaseThickness;
    const positionX = carcaseThickness;
    const positionY = leftWidth - 2;

    // makeLShapeMesh omits the carc*2 + 2 widening that makeCornerShapeMesh
    // applies, leaving these shelves ~carc*2 short of the right wall. Match it.
    const lShapeRightWidth = rightWidth + carcaseThickness * 2 + 2;

    const addMesh = (mesh: THREE.Mesh, z: number, panelThickness: number) => {
        attachEvents?.(mesh);
        mesh.position.set(
            positionX,
            positionY,
            centerPanelZ(z, panelThickness, carcaseThickness)
        );
        mesh.scale.y = -1;
        mesh.add(lineSegmentsForMesh(mesh));
        meshes.push(mesh);
    };

    // Upper section: index 0..upperCount+1 (index 0 = upper partition)
    for (let index = 0; index <= upperShelvesCount + 1; index++) {
        const selectedShelf = upperShelves[index - 1];
        const shouldApplyOffset =
            index !== 0 && index !== upperShelvesCount + 1;
        const isSolidTopSelected = index === upperShelvesCount + 1;

        let shelvePosition = Number(values.cabinet_height);
        let rightDepth = Number(values.cabinet_right_depth);
        let leftDepth = Number(values.cabinet_left_depth) - carcaseThickness;

        if (selectedShelf) {
            shelvePosition =
                heightUpper *
                    convertShelfPositionToPercentage(
                        selectedShelf.upper_shelf_position,
                        heightUpper
                    ) +
                upperShelfOffset;

            const offset = clampShelfOffset(
                shouldApplyOffset,
                selectedShelf.upper_shelf_offset
            );
            rightDepth = Number(values.cabinet_right_depth) - offset;
            leftDepth =
                Number(values.cabinet_left_depth) - carcaseThickness - offset;
        }

        if (index === 0) shelvePosition = upperShelfOffset;

        // Only real shelves take the shelf material's thickness; the
        // partitions and the top/bottom panels are carcase panels.
        const panelThickness = shouldApplyOffset
            ? shelfPanelThickness
            : carcaseThickness;
        const mesh =
            shouldApplyOffset || index === 0
                ? makeLShapeMesh({
                      leftDepth: leftDepth - 1,
                      rightDepth,
                      rightWidth: lShapeRightWidth,
                      thickness: panelThickness,
                  })
                : makeCornerShapeMesh({
                      leftDepth: leftDepth - 1,
                      rightDepth,
                      rightWidth,
                      thickness: panelThickness,
                  });

        const z =
            shelvePosition - (isSolidTopSelected ? carcaseThickness : 0) + 1;
        addMesh(mesh, z, panelThickness);

        if (onLabel) {
            if (shouldApplyOffset && selectedShelf) {
                onLabel(mesh, {
                    index,
                    shelfStyle: selectedShelf.upper_shelf_style,
                    label: `Upper Shelf ${index}`,
                    panelThickness,
                    suggestedLabelY: rightDepth,
                    suggestedLabelXOffset: -(rightWidth - leftDepth) / 2,
                });
            } else if (index === 0) {
                onLabel(mesh, {
                    index,
                    label: 'Upper Partition',
                    suggestedLabelY: rightDepth,
                    suggestedLabelXOffset: -(rightWidth - leftDepth) / 2,
                });
            }
        }
    }

    // Middle section: index 0..middleCount+1 (index 0 = upper partition underside, end = lower partition)
    for (let index = 0; index <= middleShelvesCount + 1; index++) {
        const selectedShelf = middleShelves[index - 1];
        const shouldApplyOffset =
            index !== 0 && index !== middleShelvesCount + 1;

        let shelvePosition = lowerShelfOffset;
        let rightDepth = Number(values.cabinet_right_depth);
        let leftDepth = Number(values.cabinet_left_depth) - carcaseThickness;

        if (selectedShelf) {
            shelvePosition =
                heightMiddle *
                    convertShelfPositionToPercentage(
                        selectedShelf.middle_shelf_position,
                        heightMiddle
                    ) +
                lowerShelfOffset;

            const offset = clampShelfOffset(
                shouldApplyOffset,
                selectedShelf.middle_shelf_offset
            );
            rightDepth = Number(values.cabinet_right_depth) - offset;
            leftDepth =
                Number(values.cabinet_left_depth) - carcaseThickness - offset;
        }

        if (index === 0) shelvePosition = upperShelfOffset - carcaseThickness;

        const forceLShape =
            shouldApplyOffset ||
            index === 0 ||
            index === middleShelvesCount + 1;
        const panelThickness = shouldApplyOffset
            ? shelfPanelThickness
            : carcaseThickness;
        const mesh = forceLShape
            ? makeLShapeMesh({
                  leftDepth: leftDepth - 1,
                  rightDepth,
                  rightWidth: lShapeRightWidth,
                  thickness: panelThickness,
              })
            : makeCornerShapeMesh({
                  leftDepth: leftDepth - 1,
                  rightDepth,
                  rightWidth,
                  thickness: panelThickness,
              });

        addMesh(mesh, shelvePosition + 1, panelThickness);

        if (onLabel) {
            if (
                shouldApplyOffset &&
                selectedShelf &&
                index !== middleShelvesCount + 1
            ) {
                onLabel(mesh, {
                    index,
                    shelfStyle: selectedShelf.middle_shelf_style,
                    label: `Middle Shelf ${index}`,
                    panelThickness,
                    suggestedLabelY: rightDepth,
                    suggestedLabelXOffset: -(rightWidth - leftDepth) / 2,
                });
            } else if (index === middleShelvesCount + 1) {
                onLabel(mesh, {
                    index,
                    label: 'Lower Partition',
                    suggestedLabelY: rightDepth,
                    suggestedLabelXOffset: -(rightWidth - leftDepth) / 2,
                });
            }
        }
    }

    // Lower section: index 0..lowerCount+extra (index 0 = lower partition underside)
    for (let index = 0; index <= lowerShelvesCount + lowerEndExtra; index++) {
        const selectedShelf = lowerShelves[index - 1];
        const shouldApplyOffset =
            index !== 0 && index !== lowerShelvesCount + 1;
        const isSolidTopSelected = index === upperShelvesCount + 1;

        let shelvePosition = 0;
        let rightDepth = Number(values.cabinet_right_depth);
        let leftDepth = Number(values.cabinet_left_depth) - carcaseThickness;

        if (selectedShelf) {
            shelvePosition =
                heightLower *
                convertShelfPositionToPercentage(
                    selectedShelf.lower_shelf_position,
                    heightLower
                );

            const offset = clampShelfOffset(
                shouldApplyOffset,
                selectedShelf.lower_shelf_offset
            );
            rightDepth = Number(values.cabinet_right_depth) - offset;
            leftDepth =
                Number(values.cabinet_left_depth) - carcaseThickness - offset;
        }

        // Only real shelves take the shelf material's thickness; the
        // partitions and the top/bottom panels are carcase panels.
        const panelThickness = shouldApplyOffset
            ? shelfPanelThickness
            : carcaseThickness;
        const mesh =
            shouldApplyOffset || index === 0
                ? makeLShapeMesh({
                      leftDepth: leftDepth - 1,
                      rightDepth,
                      rightWidth: lShapeRightWidth,
                      thickness: panelThickness,
                  })
                : makeCornerShapeMesh({
                      leftDepth: leftDepth - 1,
                      rightDepth,
                      rightWidth,
                      thickness: panelThickness,
                  });

        const z =
            index === 0
                ? heightLower - carcaseThickness
                : shelvePosition -
                  (isSolidTopSelected ? carcaseThickness : 0) +
                  1;
        addMesh(mesh, z, panelThickness);

        if (onLabel && shouldApplyOffset && selectedShelf) {
            onLabel(mesh, {
                index,
                shelfStyle: selectedShelf.lower_shelf_style,
                label: `Lower Shelf ${index}`,
                panelThickness,
                suggestedLabelY: rightDepth,
                suggestedLabelXOffset: -(rightWidth - leftDepth) / 2,
            });
        }
    }

    return meshes;
};

export type BuildLShapedPanelForPantryReturnParams = {
    values: PreviewFormValues;
    variables: TemplateVariable;
    carcaseThickness: number;
    // Thickness of the shelf material; falls back to the carcase thickness.
    shelfThickness?: number;
    voidWidth: number;
    returnPanelWidth: number;
    upperShelves: AdvancedUpperShelf[];
    lowerShelves: AdvancedLowerShelf[];
    upperShelvesCount: number;
    lowerShelvesCount: number;
    upperShelfOffset: number;
    heightUpperSection: number;
    isLeftReturn: boolean;
    widthOffset: number;
    makeShelfMesh: (args: {
        leftWidth: number;
        leftDepth: number;
        rightDepth: number;
        rightWidth: number;
        radius: number;
    }) => THREE.Mesh;
    makePartitionMesh: (args: {
        leftWidth: number;
        leftDepth: number;
        rightDepth: number;
        rightWidth: number;
        radius: number;
        heightOffset: number;
    }) => THREE.Mesh;
    lineSegmentsForMesh: (mesh: THREE.Mesh) => THREE.Object3D;
    attachEvents?: (mesh: THREE.Mesh) => void;
    onLabel?: (mesh: THREE.Mesh, ctx: MeshLabelHookContext) => void;
};

export const buildLShapedPanelForPantryReturn = ({
    values,
    variables,
    carcaseThickness,
    shelfThickness,
    voidWidth,
    returnPanelWidth,
    upperShelves,
    lowerShelves,
    upperShelvesCount,
    lowerShelvesCount,
    upperShelfOffset,
    heightUpperSection,
    isLeftReturn,
    makeShelfMesh,
    makePartitionMesh,
    lineSegmentsForMesh,
    attachEvents,
    onLabel,
}: BuildLShapedPanelForPantryReturnParams) => {
    const meshes: THREE.Mesh[] = [];
    const shelfPanelThickness = shelfThickness ?? carcaseThickness;

    // Span the full inner width (wall to wall); cabinet_width - widthOffset
    // left a gap short of the right inner wall.
    const rightWidth = Number(values.cabinet_width) - carcaseThickness;
    const radius = 50;
    const returnPanelReveal = 1;

    const placeReturnMesh = (
        mesh: THREE.Mesh,
        args: {
            positionX: number;
            positionY: number;
            positionZ: number;
            flipX: boolean;
        }
    ) => {
        attachEvents?.(mesh);
        if (args.flipX) mesh.scale.x = -1;
        mesh.position.set(args.positionX, args.positionY, args.positionZ);
        mesh.scale.y = -1;
        mesh.add(lineSegmentsForMesh(mesh));
        meshes.push(mesh);
    };

    // Upper shelves (type 9 only)
    for (let index = 1; index <= upperShelvesCount; index++) {
        const selectedShelf = upperShelves[index - 1];
        if (!selectedShelf || Number(selectedShelf.upper_shelf_type) !== 9)
            continue;

        const shelfOffset = clampShelfOffset(
            true,
            Number(selectedShelf.upper_shelf_offset)
        );
        const leftWidth =
            Number(values.cabinet_depth) - carcaseThickness - shelfOffset;
        const leftDepth =
            returnPanelWidth -
            voidWidth -
            shelfOffset -
            carcaseThickness -
            returnPanelReveal;
        const rightDepth = Number(values.cabinet_depth) * 0.7 - shelfOffset;

        const shelvePosition =
            heightUpperSection *
                convertShelfPositionToPercentage(
                    selectedShelf.upper_shelf_position,
                    heightUpperSection
                ) +
            upperShelfOffset;

        const mesh = makeShelfMesh({
            leftWidth,
            leftDepth,
            rightDepth,
            rightWidth,
            radius,
        });

        const flipX = !isLeftReturn;
        const positionX = flipX
            ? Number(values.cabinet_width)
            : carcaseThickness;
        const positionY = leftWidth + shelfOffset;
        placeReturnMesh(mesh, {
            positionX,
            positionY,
            positionZ: centerPanelZ(
                shelvePosition,
                shelfPanelThickness,
                carcaseThickness
            ),
            flipX,
        });

        onLabel?.(mesh, {
            index,
            shelfStyle: selectedShelf.upper_shelf_style,
            label: `Upper Shelf ${index}`,
            panelThickness: shelfPanelThickness,
            suggestedLabelY: rightDepth - 20,
        });
    }

    // Partitions
    const partitionLeftWidth = Number(values.cabinet_depth) - carcaseThickness;
    const partitionLeftDepth =
        returnPanelWidth - voidWidth - carcaseThickness - returnPanelReveal;
    const partitionRightDepth = Number(values.cabinet_depth) * 0.7;

    const upperPartitionMesh = makePartitionMesh({
        leftWidth: partitionLeftWidth,
        leftDepth: partitionLeftDepth,
        rightDepth: partitionRightDepth,
        radius,
        rightWidth,
        heightOffset: upperShelfOffset,
    });
    attachEvents?.(upperPartitionMesh);
    upperPartitionMesh.add(lineSegmentsForMesh(upperPartitionMesh));
    onLabel?.(upperPartitionMesh, {
        index: 0,
        label: 'Horizontal Partition',
        suggestedLabelY: partitionRightDepth,
    });
    meshes.push(upperPartitionMesh);

    if (variables?.is2Part) {
        const bottomPartitionMesh = makePartitionMesh({
            leftWidth: partitionLeftWidth,
            leftDepth: partitionLeftDepth,
            rightDepth: partitionRightDepth,
            radius,
            rightWidth,
            heightOffset: upperShelfOffset - carcaseThickness,
        });
        attachEvents?.(bottomPartitionMesh);
        bottomPartitionMesh.add(lineSegmentsForMesh(bottomPartitionMesh));
        meshes.push(bottomPartitionMesh);
    }

    // Lower shelves (type 9 only)
    const height2 = upperShelfOffset;
    for (let index = 1; index <= lowerShelvesCount; index++) {
        const selectedShelf = lowerShelves[index - 1];
        if (!selectedShelf || Number(selectedShelf.lower_shelf_type) !== 9)
            continue;

        const shelfOffset = clampShelfOffset(
            true,
            Number(selectedShelf.lower_shelf_offset)
        );
        const leftWidth =
            Number(values.cabinet_depth) - carcaseThickness - shelfOffset;
        const leftDepth =
            returnPanelWidth -
            voidWidth -
            shelfOffset -
            carcaseThickness -
            returnPanelReveal;
        const rightDepth = Number(values.cabinet_depth) * 0.7 - shelfOffset;

        const shelvePosition =
            height2 *
            convertShelfPositionToPercentage(
                selectedShelf.lower_shelf_position,
                height2
            );

        const mesh = makeShelfMesh({
            leftWidth,
            leftDepth,
            rightDepth,
            rightWidth,
            radius,
        });

        const flipX = !isLeftReturn;
        const positionX = flipX
            ? Number(values.cabinet_width)
            : carcaseThickness;
        const positionY = leftWidth + shelfOffset;
        placeReturnMesh(mesh, {
            positionX,
            positionY,
            positionZ: centerPanelZ(
                shelvePosition,
                shelfPanelThickness,
                carcaseThickness
            ),
            flipX,
        });

        onLabel?.(mesh, {
            index,
            shelfStyle: selectedShelf.lower_shelf_style,
            label: `Lower Shelf ${index}`,
            panelThickness: shelfPanelThickness,
            suggestedLabelY: rightDepth - 20,
        });
    }

    return meshes;
};

export type BuildBottomPanelWithVentParams = {
    values: PreviewFormValues;
    carcaseThickness: number;
    width: number;
    depth: number;
    makeBottomPanelMesh: (args: {
        width: number;
        depth: number;
        cutoutWidth: number;
        inset: number;
    }) => THREE.Mesh;
    cutoutWidth: number;
    inset: number;
    lineSegmentsForMesh: (mesh: THREE.Mesh) => THREE.Object3D;
    attachEvents?: (mesh: THREE.Mesh) => void;
};

export const buildBottomPanelWithVent = ({
    carcaseThickness,
    width,
    depth,
    makeBottomPanelMesh,
    cutoutWidth,
    inset,
    lineSegmentsForMesh,
    attachEvents,
}: BuildBottomPanelWithVentParams) => {
    const mesh = makeBottomPanelMesh({width, depth, cutoutWidth, inset});
    attachEvents?.(mesh);

    mesh.position.z = carcaseThickness;
    mesh.position.x = carcaseThickness;
    mesh.position.y = depth;
    mesh.rotation.x = Math.PI;
    mesh.add(lineSegmentsForMesh(mesh));

    return [mesh];
};

export type BuildTopBottomLShapedPanelsParams = {
    values: PreviewFormValues;
    variables?: TemplateVariable;
    carcaseThickness: number;
    height: number;
    panelCount: number;
    leftWidth: number;
    rightWidth: number;
    leftDepth: number;
    rightDepth: number;
    radius: number;
    makeLShapeMesh: (args: {
        leftWidth: number;
        rightWidth: number;
        leftDepth: number;
        rightDepth: number;
        radius: number;
    }) => THREE.Mesh;
    lineSegmentsForMesh: (mesh: THREE.Mesh) => THREE.Object3D;
    attachEvents?: (mesh: THREE.Mesh) => void;
};

export const buildTopBottomLShapedPanels = ({
    variables,
    carcaseThickness,
    height,
    panelCount,
    leftWidth,
    rightWidth,
    leftDepth,
    rightDepth,
    radius,
    makeLShapeMesh,
    lineSegmentsForMesh,
    attachEvents,
}: BuildTopBottomLShapedPanelsParams) => {
    const meshes: THREE.Mesh[] = [];

    const isUpperProduct = Boolean(variables?.isUpperProduct);

    for (let index = 0; index < panelCount; index++) {
        const panelPosition = (height - carcaseThickness) * index;
        const mesh = makeLShapeMesh({
            leftWidth,
            rightWidth,
            leftDepth,
            rightDepth,
            radius,
        });

        attachEvents?.(mesh);
        mesh.position.set(
            carcaseThickness,
            leftWidth -
                2 +
                (isUpperProduct
                    ? carcaseThickness * 2 + 2
                    : carcaseThickness * 2),
            panelPosition + 1
        );
        mesh.scale.y = -1;
        mesh.add(lineSegmentsForMesh(mesh));
        mesh.name = `LShape-TopBottom-${index === 0 ? 'bottom' : 'top'}`;
        meshes.push(mesh);
    }

    return meshes;
};

export type BuildLShapedPanelShelvesParams = {
    values: PreviewFormValues;
    variables: TemplateVariable;
    shelvesCount: number;
    shelves: AdvancedShelf[];
    carcaseThickness: number;
    // Thickness of the shelf material. Only the shelves are extruded with it —
    // the cabinet's own bottom/top panels always keep the carcase thickness.
    shelfThickness?: number;
    height: number;
    leftWidth: number;
    rightWidth: number;
    radius: number;
    isLsSolidTop: boolean;
    isOpenLShape: boolean;
    isUpper3DoorProduct: boolean;
    makeLShapeMesh: (args: {
        leftDepth: number;
        rightDepth: number;
        thickness: number;
    }) => THREE.Mesh;
    lineSegmentsForMesh: (mesh: THREE.Mesh) => THREE.Object3D;
    attachEvents?: (mesh: THREE.Mesh) => void;
    onLabel?: (mesh: THREE.Mesh, ctx: MeshLabelHookContext) => void;
};

export const buildLShapedPanelShelves = ({
    values,
    variables,
    shelvesCount,
    shelves,
    carcaseThickness,
    shelfThickness,
    height,
    leftWidth,
    rightWidth,
    isLsSolidTop,
    isOpenLShape,
    isUpper3DoorProduct,
    makeLShapeMesh,
    lineSegmentsForMesh,
    attachEvents,
    onLabel,
}: BuildLShapedPanelShelvesParams) => {
    const meshes: THREE.Mesh[] = [];
    const shelfPanelThickness = shelfThickness ?? carcaseThickness;

    const extraTop =
        isLsSolidTop || isOpenLShape || isUpper3DoorProduct ? 1 : 0;

    const isUpperProduct = Boolean(variables?.isUpperProduct);

    const useStoredPositions = hasUsableShelfPositions(
        shelves,
        shelvesCount,
        height
    );

    for (let index = 0; index < shelvesCount + 1 + extraTop; index++) {
        const leftDepthValue =
            Number(values.cabinet_left_depth) - carcaseThickness - 1 + 1;
        let rightDepth = Number(values.cabinet_right_depth) - carcaseThickness;
        let leftDepth = leftDepthValue;
        let shelvePosition = (height / (shelvesCount + 1)) * index;

        const isLsSolidTopSelected = index === shelvesCount + 1;
        const shouldApplyOffset = index !== 0 && index !== shelvesCount + 1;

        if (!values.cabinet_simple_shelves) {
            const selectedShelf = shelves?.[index - 1];
            const storedPosition =
                selectedShelf && useStoredPositions
                    ? convertShelfPositionToPercentage(
                          selectedShelf.shelf_position,
                          height
                      )
                    : 0;
            if (selectedShelf && storedPosition > 0 && storedPosition < 1) {
                shelvePosition = height * storedPosition;

                rightDepth =
                    Number(values.cabinet_right_depth) -
                    carcaseThickness -
                    (shouldApplyOffset ? selectedShelf.shelf_offset : 0);
                leftDepth =
                    leftDepthValue -
                    (shouldApplyOffset ? selectedShelf.shelf_offset : 0);
            }
        }

        // index 0 is the cabinet bottom and the solid-top cap is the cabinet
        // top: both are carcase panels and keep the carcase thickness.
        const panelThickness = shouldApplyOffset
            ? shelfPanelThickness
            : carcaseThickness;
        const mesh = makeLShapeMesh({
            leftDepth,
            rightDepth,
            thickness: panelThickness,
        });
        attachEvents?.(mesh);

        mesh.position.set(
            carcaseThickness,
            leftWidth -
                2 +
                (isUpperProduct
                    ? carcaseThickness * 2 + 2
                    : carcaseThickness * 2),
            centerPanelZ(
                shelvePosition -
                    (isLsSolidTopSelected ? carcaseThickness : 0) +
                    1,
                panelThickness,
                carcaseThickness
            )
        );
        mesh.scale.y = -1;
        mesh.name = isLsSolidTopSelected
            ? 'LShape-SolidTopCap'
            : index === 0
              ? 'LShape-PanelShelf-bottom'
              : `LShape-PanelShelf-${index}`;

        if (onLabel && shouldApplyOffset) {
            const selectedShelf = shelves?.[index - 1];
            onLabel(mesh, {
                index,
                shelfStyle: selectedShelf?.shelf_style,
                label: `Shelf ${index}`,
                panelThickness,
                suggestedLabelY: rightDepth,
                suggestedLabelXOffset: -(rightWidth - leftDepth) / 2,
            });
        }

        mesh.add(lineSegmentsForMesh(mesh));
        meshes.push(mesh);
    }

    return meshes;
};

export type BuildSingleLShapePanelParams = {
    values: PreviewFormValues;
    carcaseThickness: number;
    makeLShapeMesh: (args: {
        leftWidth: number;
        rightWidth: number;
        leftDepth: number;
        rightDepth: number;
        radius: number;
    }) => THREE.Mesh;
    lineSegmentsForMesh: (mesh: THREE.Mesh) => THREE.Object3D;
    attachEvents?: (mesh: THREE.Mesh) => void;
    centerGeometry?: boolean;
    postProcess?: (mesh: THREE.Mesh) => void;
};

export const buildSingleLShapePanel = ({
    values,
    makeLShapeMesh,
    lineSegmentsForMesh,
    attachEvents,
    centerGeometry,
    postProcess,
}: BuildSingleLShapePanelParams) => {
    const leftWidth = Number(values.cabinet_panel_width);
    const rightWidth = Number(values.cabinet_panel_length);
    const leftDepth = Number(values.cabinet_width_depth);
    const rightDepth = Number(values.cabinet_length_depth);
    const radius = Number(values.cabinet_int_radius);

    const mesh = makeLShapeMesh({
        leftWidth,
        rightWidth,
        leftDepth,
        rightDepth,
        radius,
    });

    if (centerGeometry) mesh.geometry.center();
    attachEvents?.(mesh);
    mesh.add(lineSegmentsForMesh(mesh));
    postProcess?.(mesh);

    return mesh;
};
